import React, { useMemo, useState } from 'react';

import DisplayOptions from './DisplayOptions';
import { AuthoringWorkflowState, VifAuthoring } from 'common/authoring_workflow/reducers/types';
import { Expression, FormatStyle, TableColumnFormat } from 'common/visualizations/views/agGridReact/types';
import { FILTER_FUNCTION } from 'common/components/FilterBar/SoqlFilter';
import {
  getSelectedColumnStat,
  getDefaultColumnFormat,
  isColumnFormatEqual,
} from 'common/visualizations/helpers/AgGridHelpers';
import { find, get } from 'lodash';

import { connect } from 'react-redux';
import {
  updateTableVisualizationColumnFormat,
  updateTableVisualizationColumnStyle,
} from 'common/authoring_workflow/actions';
import {
  getColumnFormats,
  getCurrentVif,
  getTableColumns,
} from 'common/authoring_workflow/selectors/vifAuthoring.js';

import './index.scss';
import StyleOptions from './StyleOptions';
import { ForgeButton, ForgeIcon } from '@tylertech/forge-react';
import { I18n } from 'common/i18n';
import ConditionalOptions from './ConditionalOptions';
import AgColumnNameSelector from '../AgGridComponents/AgColumnNameSelector';
import { defaultConditionStyle } from './helpers';
import { ViewColumn } from 'common/types/viewColumn';
import { DropdownOption } from '../AgGridComponents/AgAutocomplete';

interface IExpressionParams {
  function: string;
  arguments: {
    start?: string;
    end?: string;
  };
  precision?: number;
}

export const computeDefaultExpressionParams = async (
  vifAuthoring: VifAuthoring,
  columnName: string,
): Promise<IExpressionParams> => {
  const selectedColumns = getTableColumns(vifAuthoring);
  const vif = getCurrentVif(vifAuthoring);
  const { isDateColumn, metadata } = await getSelectedColumnStat(vif, selectedColumns, columnName);

  return {
    function: isDateColumn ? FILTER_FUNCTION.TIME_RANGE : FILTER_FUNCTION.EQUALS,
    arguments: {
      start: undefined,
      end: undefined,
    },
    precision: metadata?.format?.precision,
  };
};

export const computeDefaultColumnFormat = (
  vifAuthoring: VifAuthoring,
  columnName: string,
  expressionParams?: IExpressionParams,
): TableColumnFormat => {
  /**
   * `getTableColumns` returns `never[]` because it uses `lodash.get` in JS.
   * Explicitly casting to what it should be as a workaround.
   */
  const selectedColumns = getTableColumns(vifAuthoring) as ViewColumn[];
  const matchedColumnDetails = find(selectedColumns, { fieldName: columnName });
  // matchedColumnDetails can be `undefined` because of the find. Asserting non-null with ! as a workaround.
  const format = getDefaultColumnFormat(matchedColumnDetails!);
  const updatedExpression = {
    function: expressionParams?.function ?? FILTER_FUNCTION.EQUALS,
    arguments: {
      start: expressionParams?.arguments.start ?? undefined,
      end: expressionParams?.arguments.end ?? undefined,
      value: '',
    },
  } as Expression;

  return {
    columnName,
    displayName: '',
    conditionalType: 'condition',
    colorPalette: 'accent',
    colorPaletteType: 'default',
    format: {
      ...format,
      precision: expressionParams?.precision ?? undefined,
    },
    conditionStyle: defaultConditionStyle(),
    style: {
      backgroundColor: '#ffffff',
      textColor: '#000000',
      textFont: '',
      fontStyle: {
        isBold: false,
        isItalic: false,
        isWrapped: false,
      },
    },
    expression: updatedExpression,
    isFormatValue: true,
    isAlignHeader: false,
    isFormatTotal: false,
    isFormatSubtotal: false,
    conditionalConfig: [],
  };
};

export interface AgGridUpdatedColumnFormattingProps {
  vifAuthoring: VifAuthoring;
  onUpdateColumnFormat: (format: TableColumnFormat, columnName: string) => void;
  onUpdateColumnFormatStyle: (style: FormatStyle, columnName: string) => void;
}

const AgGridUpdatedColumnFormatting = (props: AgGridUpdatedColumnFormattingProps) => {
  const { vifAuthoring, onUpdateColumnFormat, onUpdateColumnFormatStyle } = props;
  const [columnName, setColumnName] = useState<string>('');
  const [expressionParams, setExpressionParams] = useState<IExpressionParams>();
  const defaultColumnFormat: TableColumnFormat = useMemo(() => {
    return {
      ...computeDefaultColumnFormat(vifAuthoring, columnName, expressionParams),
      conditionalConfig: [],
    };
  }, [columnName, expressionParams, vifAuthoring]);

  const columnFormats: { [key: string]: TableColumnFormat } = getColumnFormats(vifAuthoring);
  const columnFormat = { ...defaultColumnFormat, ...columnFormats[columnName] };
  /**
   * `getTableColumns` returns `never[]` because it uses `lodash.get` in JS.
   * Explicitly casting to what it should be as a workaround.
   */
  const selectedColumns = getTableColumns(vifAuthoring) as ViewColumn[];
  const currentColumn = find(selectedColumns, { fieldName: columnName });
  const currentColumnFieldName = currentColumn?.fieldName;
  const displayName = get(columnFormat, 'displayName', '');

  const onColumnNameChange = async (newColumnName: string) => {
    const newExpressionParams = await computeDefaultExpressionParams(vifAuthoring, newColumnName);
    setExpressionParams(newExpressionParams);
    setColumnName(newColumnName);
  };

  const handleColumnNameChange = (dimensionOption: DropdownOption) => {
    const updatedColumnName = get(dimensionOption, 'value');
    onColumnNameChange(updatedColumnName);
  };

  const handleUpdateColumnFormatStyle = (updatedColumnFormatStyle: FormatStyle, updatedColumn: string) => {
    onUpdateColumnFormatStyle(updatedColumnFormatStyle, updatedColumn);
  };

  const handleResetColumnFormat = () => {
    const updatedColumnFormat = { ...defaultColumnFormat, displayName };
    onUpdateColumnFormat(updatedColumnFormat, columnName);
  };

  const renderResetButton = () => {
    const scope = 'shared.visualizations.panes.presentation';
    const resetButtonLabel = I18n.t('fields.column_format.reset_to_default', { scope });

    return (
      <ForgeButton
        className="ag-grid-reset-format-button"
        aria-label={resetButtonLabel}
        onClick={handleResetColumnFormat}
        disabled={isColumnFormatEqual(columnFormat, defaultColumnFormat)}
      >
        <ForgeIcon name="refresh" />
        <span>{resetButtonLabel}</span>
      </ForgeButton>
    );
  };

  return (
    <div className="ag-grid-column-format-option">
      <div className="ag-grid-column-format-container">
        <AgColumnNameSelector
          columnName={currentColumnFieldName}
          vifAuthoring={vifAuthoring}
          onColumnNameChange={handleColumnNameChange}
        />
        {currentColumnFieldName && (
          <>
            <DisplayOptions
              tableColumnFormat={columnFormat}
              onUpdateColumnFormat={onUpdateColumnFormat}
              viewColumn={currentColumn}
            />
            <StyleOptions
              tableColumnFormat={columnFormat}
              onUpdateColumnFormat={onUpdateColumnFormat}
              viewColumn={currentColumn}
              onUpdateColumnFormatStyle={handleUpdateColumnFormatStyle}
            />
            <ConditionalOptions
              tableColumnFormat={columnFormat}
              viewColumn={currentColumn}
              onUpdateColumnFormat={onUpdateColumnFormat}
            />
            {renderResetButton()}
          </>
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  onUpdateColumnFormat: updateTableVisualizationColumnFormat,
  onUpdateColumnFormatStyle: updateTableVisualizationColumnStyle,
};

const mapStateToProps = (state: AuthoringWorkflowState) => ({
  vifAuthoring: state.vifAuthoring,
});

export default connect(mapStateToProps, mapDispatchToProps)(AgGridUpdatedColumnFormatting);
